"use client";

import { SendBotLogo } from "@sendbot/ui/SendBotLogo";
import { ChevronDown, History, Menu, Sparkles, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { LanguageSwitcher } from "@/components/language-switcher";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Link } from "@/i18n/routing";

const navLinkClass =
  "text-muted-foreground hover:text-foreground transition-colors duration-200 font-medium";

export function Header() {
  const t = useTranslations("Navbar");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      data-site-header
      className={`fixed inset-x-0 top-0 z-50 w-full border-b transition-[background-color,border-color,box-shadow] duration-200 ${
        scrolled
          ? "border-border/70 bg-background/95 shadow-[0_8px_30px_rgba(0,0,0,0.18)] backdrop-blur-xl"
          : "border-transparent bg-background/80 backdrop-blur-md"
      }`}
    >
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex h-16 w-full items-center justify-between">
          <Link
            href="/"
            className="flex flex-shrink-0 items-center space-x-3 transition-opacity hover:opacity-90"
            aria-label={t("home")}
          >
            <SendBotLogo variant="full" size={30} color="white" />
          </Link>

          <nav
            className="hidden items-center space-x-8 md:flex"
            aria-label={t("menu")}
          >
            <Link href="/enterprise" className={navLinkClass}>
              {t("enterprise")}
            </Link>
            <Link href="/gov" className={navLinkClass}>
              {t("gov")}
            </Link>
            <a
              href="https://blog.sendbot.co/"
              className={navLinkClass}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("blog")}
            </a>
            <Link href="/pricing" className={navLinkClass}>
              {t("pricing")}
            </Link>
            <Link href="/mcp" className={navLinkClass}>
              MCP
            </Link>
            <Link href="/templates" className={navLinkClass}>
              {t("templates")}
            </Link>

            <LanguageSwitcher />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2 font-medium text-muted-foreground hover:bg-accent/70 hover:text-foreground"
                >
                  {t("login")}
                  <ChevronDown className="h-4 w-4 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[220px] p-2">
                <DropdownMenuItem asChild className="cursor-pointer p-2">
                  <Link
                    href="https://studio.sendbot.co/signin"
                    className="flex flex-col items-start gap-1"
                  >
                    <span className="flex items-center gap-2 font-semibold">
                      <Sparkles className="h-4 w-4 text-primary-readable" />
                      {t("studio_title")}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {t("studio_desc")}
                    </span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild className="cursor-pointer p-2">
                  <Link
                    href="https://app.sendbot.co/signin"
                    className="flex flex-col items-start gap-1"
                  >
                    <span className="flex items-center gap-2 font-medium">
                      <History className="h-4 w-4" />
                      {t("classic_title")}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {t("classic_desc")}
                    </span>
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              asChild
              size="sm"
              className="rounded-full border border-white/10 bg-primary px-6 font-semibold text-white shadow-[0_0_20px_rgba(0,66,218,0.24)] transition-all hover:bg-primary/90 hover:shadow-[0_0_24px_rgba(0,66,218,0.34)]"
              data-umami-event="cta-click"
              data-umami-event-name="header-signup"
            >
              <Link href="https://studio.sendbot.co/register">
                {t("signup")}
              </Link>
            </Button>
          </nav>

          <button
            type="button"
            className="flex-shrink-0 rounded-md p-2 text-muted-foreground transition-colors hover:bg-accent/70 hover:text-foreground md:hidden"
            onClick={() => setIsMenuOpen((value) => !value)}
            aria-controls="primary-navigation-mobile"
            aria-label={t("menu")}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>

        {isMenuOpen && (
          <nav
            id="primary-navigation-mobile"
            className="w-full space-y-4 border-t border-border/60 py-5 md:hidden"
            aria-label={t("mobile_menu")}
          >
            <div className="flex w-full flex-col gap-1">
              {[
                ["/enterprise", t("enterprise")],
                ["/gov", t("gov")],
                ["/pricing", t("pricing")],
                ["/templates", t("templates")],
                ["/mcp", "MCP"],
              ].map(([href, label]) => (
                <Link
                  key={href}
                  href={href}
                  className="w-full rounded-md px-3 py-2.5 font-medium text-foreground transition-colors hover:bg-accent/70"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {label}
                </Link>
              ))}
              <a
                href="https://blog.sendbot.co/"
                className="w-full rounded-md px-3 py-2.5 font-medium text-foreground transition-colors hover:bg-accent/70"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsMenuOpen(false)}
              >
                {t("blog")}
              </a>
            </div>

            <div className="flex items-center justify-between border-t border-border/60 px-3 pt-4">
              <span className="font-medium text-muted-foreground">
                {t("language")}
              </span>
              <LanguageSwitcher />
            </div>

            <div className="flex flex-col gap-2 border-t border-border/60 pt-4">
              <Button asChild variant="outline" size="lg" className="w-full">
                <Link href="https://studio.sendbot.co/signin">
                  {t("login")}
                </Link>
              </Button>
              <Link
                href="https://app.sendbot.co/signin"
                className="py-1 text-center text-xs text-muted-foreground hover:text-foreground"
                onClick={() => setIsMenuOpen(false)}
              >
                {t("classic_title")}
              </Link>
              <Button
                asChild
                size="lg"
                className="w-full rounded-full border border-white/10 bg-primary font-semibold text-white shadow-[0_0_20px_rgba(0,66,218,0.22)] hover:bg-primary/90"
                data-umami-event="cta-click"
                data-umami-event-name="mobile-header-signup"
              >
                <Link href="https://studio.sendbot.co/register">
                  {t("signup")}
                </Link>
              </Button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
